/** @import modules */

import { useTranslation } from "react-i18next";
import { useParams, useNavigate, useLocation } from "react-router-dom";
import { Swiper, SwiperSlide } from "swiper/react";
import { FreeMode, Navigation } from "swiper";
import { useEffect, useState, useMemo, useCallback } from "react";

/** @import models */

import TyresAttributes from "../../Models/TyresFilters";

/** @import hooks */

import { TyresNavigator } from "../../Hooks/TyresNavigator";

/** @import components */

import ComponentLoader from "../Layout/Loaders/Loader";
import SearchableSelect from "../Shared/SearchableSelect";

/** @import assets */

import searchImage from "../../Assets/search-image-5.jpg";

import "swiper/css";
import "swiper/css/navigation";

/** Merge route segment (e.g. tyres catalog path) and ?query= for reading / writing tyre params */
function mergeTyreParamSources(routeParameters, search) {
  const out = new URLSearchParams();
  if (routeParameters) {
    const raw = String(routeParameters).startsWith("?")
      ? String(routeParameters).slice(1)
      : String(routeParameters);
    new URLSearchParams(raw).forEach((v, k) => out.set(k, v));
  }
  const q = search?.startsWith("?") ? search.slice(1) : search || "";
  new URLSearchParams(q).forEach((v, k) => out.set(k, v));
  return out;
}

function stateFromParams(params) {
  const w = params.get("w");
  const h = params.get("h");
  const d = params.get("d");
  const s = params.get("s");
  const vt = params.get("vt");

  const width =
    w != null && w !== "" && !Number.isNaN(parseInt(w, 10))
      ? parseInt(w, 10)
      : "default";
  const height =
    h != null && h !== "" && !Number.isNaN(parseFloat(h))
      ? parseFloat(h)
      : "default";
  const diameter = d != null && d !== "" ? d : "default";
  const season = s != null && s !== "" ? s.split(",")[0] : "default";
  const type = vt != null && vt !== "" ? vt : "default";

  return { width, height, diameter, season, type };
}

const HomeProductSearch = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const { parameters } = useParams();

  const { loading, data } = TyresAttributes();

  const [width, setWidth] = useState("default");
  const [height, setHeight] = useState("default");
  const [diameter, setDiameter] = useState("default");
  const [season, setSeason] = useState("default");
  const [type, setType] = useState("default");

  const mergedParams = useMemo(
    () => mergeTyreParamSources(parameters, location.search),
    [parameters, location.search]
  );

  useEffect(() => {
    const next = stateFromParams(mergedParams);
    setWidth(next.width);
    setHeight(next.height);
    setDiameter(next.diameter);
    setSeason(next.season);
    setType(next.type);
  }, [mergedParams]);

  const tireAttributes = useMemo(() => {
    if (!data?.searchAttributes?.tires?.[0]) return null;
    const base = data.searchAttributes.tires[0];
    return {
      widths: base.widths,
      heights: base.heights,
      diameters: base.diameters,
      seasons: base.seasons,
      vehicle_types: base.vehicle_types,
    };
  }, [data]);

  const sortedOptions = useMemo(() => {
    if (!tireAttributes) return null;
    return {
      widths: [...(tireAttributes.widths || [])].sort(
        (a, b) => parseInt(a.value, 10) - parseInt(b.value, 10)
      ),
      heights: [...(tireAttributes.heights || [])].sort(
        (a, b) => parseFloat(a.value) - parseFloat(b.value)
      ),
      diameters: [...(tireAttributes.diameters || [])].sort(
        (a, b) =>
          parseFloat(String(a.value).replace(/C$/i, "")) -
          parseFloat(String(b.value).replace(/C$/i, ""))
      ),
      seasons: tireAttributes.seasons || [],
    };
  }, [tireAttributes]);

  const handleChange = useCallback((field, value) => {
    const setters = {
      width: setWidth,
      height: setHeight,
      diameter: setDiameter,
      season: setSeason,
    };
    setters[field](value);
  }, []);

  const Properties = () => {
    if (loading || !sortedOptions) return <ComponentLoader />;

    const fields = [
      {
        name: t("width"),
        slug: "width",
        options: sortedOptions.widths,
        type: "INT",
        state: width,
        set: (v) => handleChange("width", v),
      },
      {
        name: t("height"),
        slug: "height",
        options: sortedOptions.heights,
        type: "FLOAT",
        state: height,
        set: (v) => handleChange("height", v),
      },
      {
        name: t("diameter"),
        slug: "diameter",
        options: sortedOptions.diameters,
        type: "TEXT",
        state: diameter,
        set: (v) => handleChange("diameter", v),
      },
      {
        name: t("season"),
        slug: "season",
        options: sortedOptions.seasons,
        type: "TEXT",
        state: season,
        set: (v) => handleChange("season", v),
      },
    ];

    return (
      <>
        {fields.map((field, index) => (
          <div className="field" key={index}>
            <div className="name hide-768">{field.name}</div>
            <div className="search-select">
              <SearchableSelect
                label={field.name}
                options={field.options}
                value={field.state}
                onChange={field.set}
                type={field.type}
                formatOption={
                  field.type === "TEXT"
                    ? (opt) => t(opt?.text || opt?.value)
                    : undefined
                }
              />
            </div>
          </div>
        ))}
      </>
    );
  };

  const search = () => {
    const paramString = mergedParams.toString();
    const object = {
      f: 21,
      l: null,
      a: null,
      b: null,
      w: width === "default" ? null : width,
      h: height === "default" ? null : height,
      d: diameter === "default" ? null : diameter,
      s: season === "default" ? null : season,
      vt: type === "default" ? null : type,
      sort: "ASC",
    };
    return navigate(
      `/shop/tyres/${new URLSearchParams(
        TyresNavigator(paramString, object)
      ).toString()}`
    );
  };

  if (!data?.searchAttributes?.tires?.[0]) {
    return (
      <div className="search">
        <img src={searchImage} alt="product-search" />
        <div className="content max-content-width">
          <ComponentLoader />
        </div>
      </div>
    );
  }

  const vehicleTypes =
    tireAttributes?.vehicle_types || data.searchAttributes.tires[0].vehicle_types;

  return (
    <div className="search">
      <img src={searchImage} alt="product-search" />
      <div className="content max-content-width">
        <div className="search-heading">
          <h1>{t("search_tires")}</h1>
        </div>
        <div className="properties">
          <Properties />
        </div>
        <div className="action">
          <button onClick={search}>{t("search")}</button>
        </div>
        <Swiper
          slidesPerView={1}
          freeMode
          navigation
          modules={[Navigation, FreeMode]}
          className="swiper-vehicle-types"
        >
          <SwiperSlide>
            <div className="type-option">
              <input
                type="radio"
                id="default"
                name="mobile_vehicle_types"
                value="default"
                checked={type === "default"}
                onChange={() => setType("default")}
              />
              <label htmlFor="default">{t("all")}</label>
            </div>
          </SwiperSlide>
          {vehicleTypes.map((vType, index) => (
            <SwiperSlide key={index}>
              <div className="type-option">
                <input
                  type="radio"
                  id={vType.value}
                  name="mobile_vehicle_types"
                  value={vType.value}
                  checked={type === vType.value}
                  onChange={() => setType(vType.value)}
                />
                <label htmlFor={vType.value}>{t(vType.value)}</label>
              </div>
            </SwiperSlide>
          ))}
        </Swiper>
        <div className="vehicle-type">
          <div className="type-option">
            <input
              type="radio"
              id="default-desktop"
              name="vehicle_types"
              value="default"
              checked={type === "default"}
              onChange={() => setType("default")}
            />
            <label htmlFor="default-desktop">{t("all")}</label>
          </div>
          {vehicleTypes.map((vType, index) => (
            <div className="type-option" key={index}>
              <input
                type="radio"
                id={`${vType.value}-desktop`}
                name="vehicle_types"
                value={vType.value}
                checked={type === vType.value}
                onChange={() => setType(vType.value)}
              />
              <label htmlFor={`${vType.value}-desktop`}>{t(vType.value)}</label>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/** @export component */

export default HomeProductSearch;
